import {useEffect} from 'react';
import {fetchEmployment, fetchIdentificationInfo} from '@rocket-ui/lk-api';
import {
	applyApiPhotosToDocumentsUpload,
	createEmptyDocumentsUploadValues,
	extractIdentificationDocumentPhotos,
	extractRegistrationBackendFlags,
	getInitialPaymentSubStep,
	getStatusConnectionPaymentMethods,
	isDocumentsRegistrationStep,
	mapEmploymentsToWorkStatusConnections,
	mergeWorkStatusConnections,
	combinesDocumentPhotosWithDetails,
	RegistrationStep,
	usesForeignDocumentDetailsForm,
	WORK_STATUS_STEP_ENABLED,
} from 'features/registration';
import {revokePreviewUrl} from './helpers';
import type {RegistrationWizardState} from './useRegistrationWizardState';

/** Подгрузка данных с бэка, сброс загруженных документов и скролл при смене шага. */
export const useRegistrationEffects = (state: RegistrationWizardState) => {
	const {
		documentsFlow,
		documentsFlowKey,
		identificationDocumentPhotos,
		paymentSubStep,
		pendingPaymentMethod,
		personalInfo,
		preserveDocumentsFlowKeyRef,
		registrationFlow,
		returnToIdentityReview,
		setBackendFlags,
		setDocumentsUpload,
		setIdentificationDocumentPhotos,
		setPaymentSubStep,
		setPendingPaymentMethod,
		setStep,
		setWorkStatusConnections,
		setWorkStatusesReady,
		step,
		workStatus,
	} = state;

	const firstDocumentsStep =
		usesForeignDocumentDetailsForm(personalInfo.citizenshipOksmCode, personalInfo.residenceOksmCode) &&
		!combinesDocumentPhotosWithDetails(personalInfo.citizenshipOksmCode)
			? RegistrationStep.DocumentPhotos
			: RegistrationStep.DocumentDetails;

	useEffect(() => {
		if (!WORK_STATUS_STEP_ENABLED || step !== RegistrationStep.WorkStatus) {
			return;
		}

		let cancelled = false;

		void fetchEmployment()
			.then(({data}) => {
				if (cancelled) {
					return;
				}

				const fromApi = mapEmploymentsToWorkStatusConnections(data);
				setWorkStatusConnections((current) => mergeWorkStatusConnections(fromApi, current));
				setWorkStatusesReady(true);
			})
			.catch(() => {
				if (!cancelled) {
					setWorkStatusesReady(true);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [step]);

	useEffect(() => {
		if (preserveDocumentsFlowKeyRef.current === documentsFlowKey) {
			return;
		}

		preserveDocumentsFlowKeyRef.current = null;

		setDocumentsUpload((current) => {
			for (const slot of Object.values(current.slots)) {
				revokePreviewUrl(slot.previewUrl);
			}

			return createEmptyDocumentsUploadValues(documentsFlow);
		});

		setStep((current) => {
			/** Вход с профиля `?edit=photos` — не уводить РФ с DocumentPhotos на DocumentDetails. */
			if (returnToIdentityReview && current === RegistrationStep.DocumentPhotos) {
				return current;
			}

			/**
			 * «Подтвердите место проживания» стоит после налогового идентификатора.
			 * Сброс флоу документов не должен возвращать на экран данных раньше него.
			 */
			if (current === RegistrationStep.ResidenceDocument || current === RegistrationStep.IdentificationInn) {
				return current;
			}

			return isDocumentsRegistrationStep(current) || current === RegistrationStep.IdentityReview
				? firstDocumentsStep
				: current;
		});
	}, [documentsFlowKey, firstDocumentsStep, returnToIdentityReview]);

	useEffect(() => {
		const scrollRoot = document.querySelector('[data-registration-scroll]');

		if (scrollRoot instanceof HTMLElement) {
			scrollRoot.scrollTop = 0;
		}

		window.scrollTo(0, 0);
	}, [step, paymentSubStep]);

	useEffect(() => {
		if (
			step !== RegistrationStep.IdentityReview &&
			step !== RegistrationStep.DocumentPhotos &&
			step !== RegistrationStep.DocumentDetails &&
			step !== RegistrationStep.ResidenceDocument
		) {
			return;
		}

		let cancelled = false;

		void fetchIdentificationInfo()
			.then(({data}) => {
				if (cancelled) {
					return;
				}

				setBackendFlags(extractRegistrationBackendFlags(data));
				setIdentificationDocumentPhotos(extractIdentificationDocumentPhotos(data));
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, [step]);

	// Ранее загруженные фото документов — в пустые слоты (в т.ч. после сброса слотов при смене флоу).
	useEffect(() => {
		setDocumentsUpload((current) =>
			applyApiPhotosToDocumentsUpload(documentsFlow, current, identificationDocumentPhotos),
		);
	}, [documentsFlowKey, identificationDocumentPhotos]);

	// Если способ оплаты один (ИП — «По номеру счёта»), экран выбора не нужен — сразу к вводу реквизитов.
	useEffect(() => {
		if (step !== RegistrationStep.PaymentMethod || pendingPaymentMethod || paymentSubStep !== 'select') {
			return;
		}

		const methods = getStatusConnectionPaymentMethods(workStatus, registrationFlow.paymentMethods);

		if (methods.length === 1) {
			setPendingPaymentMethod(methods[0]);
			setPaymentSubStep(getInitialPaymentSubStep(methods[0]));
		}
	}, [step, pendingPaymentMethod, paymentSubStep, workStatus, registrationFlow.paymentMethods]);
};
